import { basicSetup } from "codemirror";
import { Compartment, EditorState, StateEffect, StateField } from "@codemirror/state";
import { Decoration, EditorView, keymap } from "@codemirror/view";
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate, removeAwarenessStates } from "y-protocols/awareness";
import { yCollab, yUndoManagerKeymap } from "y-codemirror.next";
import { parse } from "../src/parser.js";
import { renderHtml } from "../src/render-html.js";
import { convertTrelbyToCue } from "../src/trelby.js";
import { MAX_SOURCE_BYTES } from "../src/chunks.js";
import {
  binaryCollaborationFrame,
  COLLAB_AWARENESS,
  COLLAB_CONTROL,
  COLLAB_STATE_VECTOR,
  COLLAB_SYNC,
  COLLAB_UPDATE,
  decodeCollaborationFrame,
  decodeCollaborationJson,
  jsonCollaborationFrame
} from "../src/collaboration-protocol.js";

const BLANK_SOURCE = `---
title: Untitled Screenplay
---

.INT. LOCATION - DAY

`;
const PREVIEW_DELAY = 120;
const UPDATE_BATCH_DELAY = 75;
const APP_BASE = location.pathname === "/cue" || location.pathname.startsWith("/cue/") ? "/cue" : "";
const encoder = new TextEncoder();
const setCommentDecorations = StateEffect.define();
const commentDecorations = StateField.define({
  create: () => Decoration.none,
  update(value, transaction) {
    let decorations = value.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (effect.is(setCommentDecorations)) decorations = effect.value;
    }
    return decorations;
  },
  provide: (field) => EditorView.decorations.from(field)
});

const elements = {
  editor: document.querySelector("#source-editor"),
  documentName: document.querySelector("#document-name"),
  documentList: document.querySelector("#document-list"),
  sharedDocumentList: document.querySelector("#shared-document-list"),
  sharedLibraryGroup: document.querySelector("#shared-library-group"),
  libraryCount: document.querySelector("#library-count"),
  saveState: document.querySelector("#save-state"),
  saveLabel: document.querySelector("#save-label"),
  preview: document.querySelector("#preview-frame"),
  sourceStats: document.querySelector("#source-stats"),
  fileInput: document.querySelector("#file-input"),
  toast: document.querySelector("#toast"),
  libraryPanel: document.querySelector("#library-panel"),
  mobileLibraryButton: document.querySelector("#mobile-library-button"),
  splitWorkspace: document.querySelector(".split-workspace"),
  presenceList: document.querySelector("#presence-list"),
  shareButton: document.querySelector("#share-document"),
  shareDialog: document.querySelector("#share-dialog"),
  shareList: document.querySelector("#share-list"),
  shareLinkList: document.querySelector("#share-link-list"),
  newShareLink: document.querySelector("#new-share-link"),
  newShareLinkUrl: document.querySelector("#new-share-link-url"),
  accessDialog: document.querySelector("#access-dialog"),
  accessMessage: document.querySelector("#access-message"),
  commentsToggle: document.querySelector("#comments-toggle"),
  commentsPanel: document.querySelector("#comments-panel"),
  commentsList: document.querySelector("#comments-list"),
  commentCount: document.querySelector("#comment-count"),
  addComment: document.querySelector("#add-comment"),
  commentForm: document.querySelector("#comment-form"),
  commentBody: document.querySelector("#comment-body"),
  commentQuote: document.querySelector("#comment-quote"),
  showResolved: document.querySelector("#show-resolved")
};

const state = {
  documents: [],
  current: null,
  view: null,
  ydoc: null,
  ytext: null,
  metadata: null,
  awareness: null,
  persistence: null,
  undoManager: null,
  socket: null,
  role: "viewer",
  connectionToken: 0,
  reconnectTimer: null,
  reconnectAttempts: 0,
  authorizationRenewTimer: null,
  reauthorizing: false,
  updateTimer: null,
  pendingUpdates: [],
  previewTimer: null,
  previewTargetLine: 1,
  applyingRemoteUpdate: false,
  fileMode: "cue",
  toastTimer: null,
  accessChanged: false,
  accountEmail: null,
  comments: [],
  canComment: false,
  pendingComment: null,
  readOnlyCompartment: new Compartment(),
  editableCompartment: new Compartment()
};

bindEvents();
renderPreview(BLANK_SOURCE);
void initialize();

async function initialize() {
  setConnectionState("connecting", "Connecting");
  try {
    const sharedDocument = await exchangeShareFragment();
    if (sharedDocument) {
      state.documents = [summaryFromDocument(sharedDocument)];
      renderLibrary();
      await openDocument(sharedDocument.id);
      return;
    }

    await refreshLibrary();
    const requestedId = new URLSearchParams(location.search).get("document");
    const target = requestedId && state.documents.some((item) => item.id === requestedId)
      ? requestedId
      : state.documents[0]?.id;
    if (target) {
      await openDocument(target);
    } else {
      const created = await createDocument("Untitled Screenplay", BLANK_SOURCE);
      await openDocument(created.id, created);
    }
  } catch (error) {
    if (error instanceof ApiRequestError && (error.status === 401 || error.status === 402)) {
      redirectToAccount();
      return;
    }
    handleNetworkError(error, "Cue could not load your library.");
  }
}

function bindEvents() {
  elements.preview.addEventListener("load", scrollPreviewToLocalEdit);
  elements.documentName.addEventListener("input", () => {
    if (!state.current || !canEdit()) return;
    const name = elements.documentName.value.slice(0, 120);
    if (name.trim()) state.metadata?.set("name", name);
    state.current.name = name;
    renderLibrary();
  });
  elements.documentName.addEventListener("blur", () => {
    if (canEdit() && !elements.documentName.value.trim()) {
      state.metadata?.set("name", "Untitled Screenplay");
    }
  });

  document.querySelector("#new-document").addEventListener("click", () => void newDocument());
  document.querySelector("#save-now").addEventListener("click", () => void saveCurrent());
  document.querySelector("#duplicate-document").addEventListener("click", () => void duplicateDocument());
  document.querySelector("#delete-document").addEventListener("click", () => void deleteCurrentDocument());
  document.querySelector("#download-cue").addEventListener("click", downloadCue);
  document.querySelector("#download-html").addEventListener("click", downloadHtml);
  document.querySelector("#print-preview").addEventListener("click", printPreview);
  elements.shareButton.addEventListener("click", () => void openShareDialog());
  elements.commentsToggle.addEventListener("click", toggleCommentsPanel);
  document.querySelector("#close-comments").addEventListener("click", closeCommentsPanel);
  elements.addComment.addEventListener("click", startComment);
  document.querySelector("#cancel-comment").addEventListener("click", cancelComment);
  elements.commentForm.addEventListener("submit", (event) => void submitComment(event));
  elements.showResolved.addEventListener("change", renderComments);

  document.querySelector("#open-cue").addEventListener("click", () => chooseFile("cue"));
  document.querySelector("#import-trelby").addEventListener("click", () => chooseFile("trelby"));
  elements.fileInput.addEventListener("change", () => void importSelectedFile());
  for (const button of document.querySelectorAll("[data-insert]")) {
    button.addEventListener("click", () => insertCueBlock(button.dataset.insert, Number(button.dataset.caretBack ?? 0)));
  }

  elements.mobileLibraryButton.addEventListener("click", toggleMobileLibrary);
  for (const tab of document.querySelectorAll("[data-mobile-view]")) {
    if (tab === elements.splitWorkspace) continue;
    tab.addEventListener("click", () => setMobileView(tab.dataset.mobileView));
  }

  document.querySelector("#close-share-dialog").addEventListener("click", () => elements.shareDialog.close());
  document.querySelector("#invite-form").addEventListener("submit", (event) => void inviteCollaborator(event));
  document.querySelector("#link-form").addEventListener("submit", (event) => void createShareLink(event));
  document.querySelector("#copy-share-link").addEventListener("click", () => void copyNewShareLink());
  document.querySelector("#download-access-copy").addEventListener("click", downloadCue);
  document.querySelector("#save-access-copy").addEventListener("click", () => void saveAccessCopy());

  window.addEventListener("online", () => connectCollaboration());
  window.addEventListener("offline", () => setConnectionState("offline", "Offline"));
  window.addEventListener("keydown", handleKeyboardShortcut);
}

async function exchangeShareFragment() {
  const isShareEntry =
    new URLSearchParams(location.search).get("share") === "1"
    || location.pathname.endsWith("/share")
    || location.pathname.endsWith("/share/");
  if (!location.hash || !isShareEntry) return null;
  const token = location.hash.slice(1);
  if (!/^[A-Za-z0-9_-]{43}$/.test(token)) return null;
  try {
    const payload = await api("/api/share-links/exchange", {
      method: "POST",
      body: JSON.stringify({ token })
    });
    history.replaceState(null, "", `${APP_BASE}/?document=${encodeURIComponent(payload.document.id)}`);
    return payload.document;
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 401) redirectToAccount();
    throw error;
  }
}

function redirectToAccount() {
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  location.assign(`${APP_BASE}/account/?return_to=${encodeURIComponent(returnTo)}`);
}

async function refreshLibrary() {
  const payload = await api("/api/documents");
  state.documents = payload.documents;
  renderLibrary();
}

async function openDocument(id, suppliedDocument = null) {
  if (state.current?.id === id && !suppliedDocument) {
    closeMobileLibrary();
    return;
  }
  teardownCollaboration();
  const token = ++state.connectionToken;
  const documentData = suppliedDocument ?? (await api(`/api/documents/${encodeURIComponent(id)}`)).document;
  if (token !== state.connectionToken) return;

  state.current = documentData;
  state.role = documentData.effectiveRole || documentData.grantedRole || "owner";
  state.accessChanged = false;
  state.previewTargetLine = 1;
  elements.documentName.value = documentData.name;
  setupCollaboration(documentData, token);
  updateRoleUi();
  upsertSummary(documentData);
  renderLibrary();
  renderPreview(documentData.source);
  updateStats(documentData.source);
  closeMobileLibrary();
  closeCommentsPanel();
  history.replaceState(null, "", `${APP_BASE}/?document=${encodeURIComponent(id)}`);
  connectCollaboration(token);
  void refreshComments();
}

function setupCollaboration(documentData, token) {
  const ydoc = new Y.Doc();
  const ytext = ydoc.getText("source");
  const metadata = ydoc.getMap("metadata");
  const awareness = new Awareness(ydoc);
  const undoManager = new Y.UndoManager(ytext);
  state.ydoc = ydoc;
  state.ytext = ytext;
  state.metadata = metadata;
  state.awareness = awareness;
  state.undoManager = undoManager;

  awareness.setLocalStateField("user", {
    name: localDisplayName(),
    color: colorFor(String(ydoc.clientID))
  });

  const extensions = [
    keymap.of(yUndoManagerKeymap),
    basicSetup,
    yCollab(ytext, awareness, { undoManager }),
    state.readOnlyCompartment.of(EditorState.readOnly.of(!canEdit())),
    state.editableCompartment.of(EditorView.editable.of(canEdit())),
    EditorView.lineWrapping,
    commentDecorations,
    EditorView.updateListener.of((update) => {
      if (update.docChanged) onEditorChanged();
    })
  ];
  state.view = new EditorView({
    state: EditorState.create({ doc: "", extensions }),
    parent: elements.editor
  });

  ydoc.on("update", (update, origin) => {
    if (token !== state.connectionToken || origin === "remote") return;
    if (canEdit()) queueUpdate(update);
  });
  metadata.observe(() => {
    if (token !== state.connectionToken) return;
    const name = normalizedName(metadata.get("name") || documentData.name);
    state.current.name = name;
    if (document.activeElement !== elements.documentName) elements.documentName.value = name;
    renderLibrary();
  });
  awareness.on("update", ({ added, updated, removed }, origin) => {
    renderPresence();
    if (origin === "remote" || !state.socket || state.socket.readyState !== WebSocket.OPEN) return;
    const clients = added.concat(updated, removed);
    if (clients.length) {
      state.socket.send(binaryCollaborationFrame(COLLAB_AWARENESS, encodeAwarenessUpdate(awareness, clients)));
    }
  });

  if (canEdit()) {
    state.persistence = new IndexeddbPersistence(`cue-collaboration:${documentData.id}`, ydoc);
  }
  setConnectionState("connecting", "Connecting");
}

function teardownCollaboration() {
  clearTimeout(state.reconnectTimer);
  clearTimeout(state.authorizationRenewTimer);
  clearTimeout(state.updateTimer);
  state.connectionToken += 1;
  state.socket?.close(1000, "Switching documents");
  state.persistence?.destroy();
  state.view?.destroy();
  state.awareness?.destroy();
  state.ydoc?.destroy();
  elements.editor.replaceChildren();
  Object.assign(state, {
    view: null,
    ydoc: null,
    ytext: null,
    metadata: null,
    awareness: null,
    persistence: null,
    undoManager: null,
    socket: null,
    pendingUpdates: [],
    updateTimer: null,
    reconnectAttempts: 0,
    authorizationRenewTimer: null,
    reauthorizing: false,
    applyingRemoteUpdate: false,
    accountEmail: null,
    comments: [],
    canComment: false,
    pendingComment: null
  });
  renderComments();
  renderPresence();
}

function connectCollaboration(token = state.connectionToken) {
  if (!state.current || !state.ydoc || token !== state.connectionToken || state.accessChanged) return;
  if (!navigator.onLine) {
    setConnectionState("offline", "Offline");
    return;
  }
  if (state.socket && (state.socket.readyState === WebSocket.OPEN || state.socket.readyState === WebSocket.CONNECTING)) return;

  setConnectionState("connecting", "Connecting");
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const socket = new WebSocket(
    `${protocol}//${location.host}${APP_BASE}/api/documents/${encodeURIComponent(state.current.id)}/collaboration`
  );
  socket.binaryType = "arraybuffer";
  state.socket = socket;

  socket.addEventListener("open", () => {
    if (token !== state.connectionToken) return socket.close();
    state.reconnectAttempts = 0;
    socket.send(jsonCollaborationFrame({ type: "hello", awarenessClientId: state.awareness.clientID }));
    const clients = [...state.awareness.getStates().keys()];
    socket.send(binaryCollaborationFrame(COLLAB_AWARENESS, encodeAwarenessUpdate(state.awareness, clients)));
  });
  socket.addEventListener("message", (event) => {
    if (token === state.connectionToken) handleCollaborationMessage(event.data);
  });
  socket.addEventListener("close", (event) => {
    if (token !== state.connectionToken || state.socket !== socket) return;
    state.socket = null;
    clearTimeout(state.authorizationRenewTimer);
    state.authorizationRenewTimer = null;
    if (state.reauthorizing || /authorization expired/i.test(event.reason)) {
      state.reauthorizing = false;
      setConnectionState("connecting", "Connecting");
      scheduleReconnect(token, 0);
      return;
    }
    if (event.code === 4003 || event.code === 4004) {
      handleAccessChanged(event.reason || "Your access changed.");
      return;
    }
    setConnectionState("offline", "Offline");
    scheduleReconnect(token);
  });
  socket.addEventListener("error", () => {
    if (token === state.connectionToken) setConnectionState("offline", "Offline");
  });
}

function handleCollaborationMessage(value) {
  const { type, payload } = decodeCollaborationFrame(value);
  if (type === COLLAB_SYNC || type === COLLAB_UPDATE) {
    applyRemoteUpdate(payload);
    setConnectionState("synced", "Synced");
    return;
  }
  if (type === COLLAB_STATE_VECTOR) {
    if (canEdit()) {
      const update = Y.encodeStateAsUpdate(state.ydoc, payload);
      if (update.byteLength > 2) state.socket.send(binaryCollaborationFrame(COLLAB_UPDATE, update));
    }
    setConnectionState("synced", "Synced");
    return;
  }
  if (type === COLLAB_AWARENESS) {
    applyAwarenessUpdate(state.awareness, payload, "remote");
    return;
  }
  if (type !== COLLAB_CONTROL) return;

  const control = decodeCollaborationJson(payload);
  if (control.type === "ready") {
    state.role = control.role;
    state.accountEmail = control.email || null;
    state.reauthorizing = false;
    state.awareness.setLocalStateField("user", {
      name: control.name || "You",
      color: state.awareness.getLocalState()?.user?.color || colorFor(String(state.ydoc.clientID))
    });
    updateRoleUi();
    updateCommentsUi();
    scheduleAuthorizationRefresh(control.authorizationExpiresAt);
    setConnectionState("synced", "Synced");
  } else if (control.type === "ack" || control.type === "checkpoint") {
    if (control.revision) state.current.revision = control.revision;
    setConnectionState("synced", "Synced");
  } else if (control.type === "presence-left") {
    removeAwarenessStates(state.awareness, [control.clientId], "remote");
  } else if (control.type === "comments-changed") {
    void refreshComments();
  } else if (control.type === "source-too-large") {
    showToast("This screenplay has reached the 3 MiB limit.");
  } else if (control.type === "authorization-expired") {
    state.reauthorizing = true;
    setConnectionState("connecting", "Connecting");
  } else if (control.type === "access-changed" || control.type === "deleted") {
    handleAccessChanged(control.reason || "Your access changed.");
  }
}

function applyRemoteUpdate(update) {
  state.applyingRemoteUpdate = true;
  try {
    Y.applyUpdate(state.ydoc, update, "remote");
  } finally {
    state.applyingRemoteUpdate = false;
  }
  updateCommentMarks();
}

function queueUpdate(update) {
  state.pendingUpdates.push(update);
  setConnectionState(navigator.onLine ? "connecting" : "offline", navigator.onLine ? "Connecting" : "Offline");
  clearTimeout(state.updateTimer);
  state.updateTimer = setTimeout(flushUpdates, UPDATE_BATCH_DELAY);
}

function flushUpdates() {
  clearTimeout(state.updateTimer);
  state.updateTimer = null;
  if (!state.pendingUpdates.length) return;
  const update = Y.mergeUpdates(state.pendingUpdates);
  state.pendingUpdates = [];
  if (state.socket?.readyState === WebSocket.OPEN && canEdit()) {
    state.socket.send(binaryCollaborationFrame(COLLAB_UPDATE, update));
  }
}

function scheduleReconnect(token, requestedDelay = null) {
  clearTimeout(state.reconnectTimer);
  const delay = requestedDelay ?? Math.min(10_000, 800 * 2 ** state.reconnectAttempts++);
  state.reconnectTimer = setTimeout(() => connectCollaboration(token), delay);
}

function scheduleAuthorizationRefresh(expiresAt) {
  clearTimeout(state.authorizationRenewTimer);
  const expiration = Number(expiresAt);
  if (!Number.isFinite(expiration)) return;
  const refreshIn = Math.max(60_000, expiration - Date.now() - 5 * 60 * 1000);
  state.authorizationRenewTimer = setTimeout(() => {
    if (!state.socket || state.socket.readyState !== WebSocket.OPEN) return;
    state.reauthorizing = true;
    setConnectionState("connecting", "Connecting");
    state.socket.close(1000, "Refreshing authorization");
  }, refreshIn);
}

function onEditorChanged() {
  if (!state.current) return;
  const source = currentSource();
  state.current.source = source;
  if (encoder.encode(source).byteLength > MAX_SOURCE_BYTES) {
    state.undoManager?.undo();
    showToast("Cue screenplays are limited to 3 MiB.");
    return;
  }
  if (!state.applyingRemoteUpdate && state.view?.hasFocus) {
    const position = state.view.state.selection.main.head;
    state.previewTargetLine = state.view.state.doc.lineAt(position).number;
  }
  schedulePreview();
  updateStats(source);
}

async function saveCurrent() {
  if (!state.current || !canEdit()) return;
  flushUpdates();
  setConnectionState("connecting", "Connecting");
  try {
    const payload = await api(`/api/documents/${encodeURIComponent(state.current.id)}/checkpoint`, {
      method: "POST"
    });
    state.current.revision = payload.document.revision;
    state.current.updatedAt = new Date().toISOString();
    upsertSummary(state.current);
    renderLibrary();
    setConnectionState("synced", "Synced");
  } catch (error) {
    handleNetworkError(error, "The checkpoint could not be saved.");
  }
}

async function createDocument(name, source) {
  const payload = await api("/api/documents", {
    method: "POST",
    body: JSON.stringify({ name, source })
  });
  const documentData = {
    ...payload.document,
    ownerEmail: payload.document.ownerEmail || "",
    grantedRole: "owner",
    effectiveRole: "owner",
    shared: false
  };
  state.documents.unshift(summaryFromDocument(documentData));
  renderLibrary();
  return documentData;
}

async function newDocument() {
  const created = await createDocument("Untitled Screenplay", BLANK_SOURCE);
  await openDocument(created.id, created);
}

async function duplicateDocument() {
  if (!state.current || state.role === "viewer") return;
  const created = await createDocument(`${normalizedName(state.current.name)} Copy`, currentSource());
  await openDocument(created.id, created);
}

async function deleteCurrentDocument() {
  if (!state.current || state.role !== "owner") return;
  const name = normalizedName(state.current.name);
  if (!window.confirm(`Delete “${name}”? This cannot be undone.`)) return;
  const deletedId = state.current.id;
  teardownCollaboration();
  await api(`/api/documents/${encodeURIComponent(deletedId)}`, { method: "DELETE" });
  state.documents = state.documents.filter((item) => item.id !== deletedId);
  state.current = null;
  if (state.documents.length) {
    await openDocument(state.documents[0].id);
  } else {
    const created = await createDocument("Untitled Screenplay", BLANK_SOURCE);
    await openDocument(created.id, created);
  }
}

function chooseFile(mode) {
  state.fileMode = mode;
  elements.fileInput.accept = mode === "trelby" ? ".trelby,text/plain" : ".cue,text/plain";
  elements.fileInput.value = "";
  elements.fileInput.click();
}

async function importSelectedFile() {
  const file = elements.fileInput.files?.[0];
  if (!file) return;
  try {
    const raw = await file.text();
    let source = raw;
    if (state.fileMode === "trelby") {
      const converted = convertTrelbyToCue(raw, { filename: file.name });
      source = converted.cue;
      if (converted.warnings.length) {
        showToast(`Imported with ${converted.warnings.length} conversion warning${converted.warnings.length === 1 ? "" : "s"}.`);
      }
    }
    const created = await createDocument(deriveDocumentName(source, file.name), source);
    await openDocument(created.id, created);
  } catch (error) {
    showToast(error instanceof Error ? error.message : "The file could not be imported.");
  } finally {
    elements.fileInput.value = "";
  }
}

function insertCueBlock(template, caretBack) {
  if (!state.view || !canEdit()) return;
  const selection = state.view.state.selection.main;
  const source = state.view.state.doc.toString();
  const before = source.slice(0, selection.from);
  const after = source.slice(selection.to);
  const prefix = before.length === 0 || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
  const suffix = after.length === 0 || after.startsWith("\n\n") ? "" : after.startsWith("\n") ? "\n" : "\n\n";
  const insertion = `${prefix}${template}${suffix}`;
  const caret = selection.from + insertion.length - suffix.length - caretBack;
  state.view.dispatch({
    changes: { from: selection.from, to: selection.to, insert: insertion },
    selection: { anchor: Math.max(selection.from, caret) },
    scrollIntoView: true
  });
  state.view.focus();
}

function schedulePreview() {
  clearTimeout(state.previewTimer);
  state.previewTimer = setTimeout(() => renderPreview(), PREVIEW_DELAY);
}

function renderPreview(source = currentSource()) {
  try {
    elements.preview.srcdoc = renderHtml(parse(source));
  } catch (error) {
    elements.preview.srcdoc = `<!doctype html><meta charset="utf-8"><style>
      body{padding:2rem;font:14px/1.5 ui-monospace,monospace;background:#fff7f4;color:#5b1d16}
      pre{white-space:pre-wrap}</style><h1>Preview error</h1><pre>${escapeHtml(
        error instanceof Error ? error.message : String(error)
      )}</pre>`;
  }
}

function scrollPreviewToLocalEdit() {
  const frameDocument = elements.preview.contentDocument;
  const frameWindow = elements.preview.contentWindow;
  if (!frameDocument || !frameWindow) return;
  const requestedLine = state.previewTargetLine || 1;
  const candidates = [...frameDocument.querySelectorAll("[data-cue-line]")]
    .map((element) => ({ element, line: Number(element.dataset.cueLine) }))
    .filter((candidate) => Number.isFinite(candidate.line))
    .sort((left, right) => left.line - right.line);
  let target = candidates[0]?.element;
  for (const candidate of candidates) {
    if (candidate.line > requestedLine) break;
    target = candidate.element;
  }
  if (!target) return;
  const top = target.getBoundingClientRect().top + frameWindow.scrollY - frameWindow.innerHeight * 0.35;
  frameWindow.scrollTo({ top: Math.max(0, top), behavior: "auto" });
}

function renderLibrary() {
  renderDocumentGroup(elements.documentList, state.documents.filter((item) => !item.shared));
  const shared = state.documents.filter((item) => item.shared);
  renderDocumentGroup(elements.sharedDocumentList, shared);
  elements.sharedLibraryGroup.hidden = shared.length === 0;
  const count = state.documents.length;
  elements.libraryCount.textContent = `${count} document${count === 1 ? "" : "s"}`;
}

function renderDocumentGroup(container, documents) {
  container.replaceChildren();
  const sorted = [...documents].sort((left, right) =>
    String(right.updatedAt || "").localeCompare(String(left.updatedAt || ""))
  );
  for (const item of sorted) {
    const listItem = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    if (state.current?.id === item.id) button.setAttribute("aria-current", "page");
    const name = document.createElement("strong");
    name.textContent = item.id === state.current?.id ? normalizedName(state.current.name) : item.name;
    const detail = document.createElement("span");
    detail.className = "document-detail";
    detail.textContent = item.shared
      ? `${capitalize(item.effectiveRole || item.grantedRole)} · ${item.ownerEmail}`
      : formatRelativeTime(item.updatedAt);
    button.append(name, detail);
    button.addEventListener("click", () => void openDocument(item.id));
    listItem.append(button);
    container.append(listItem);
  }
}

function updateRoleUi() {
  const editable = canEdit();
  elements.documentName.disabled = !editable;
  elements.shareButton.hidden = state.role !== "owner";
  document.querySelector("#delete-document").hidden = state.role !== "owner";
  document.querySelector("#duplicate-document").disabled = state.role === "viewer";
  document.querySelector("#save-now").disabled = !editable;
  for (const button of document.querySelectorAll("[data-insert]")) button.disabled = !editable;
  if (state.view) {
    state.view.dispatch({
      effects: [
        state.readOnlyCompartment.reconfigure(EditorState.readOnly.of(!editable)),
        state.editableCompartment.reconfigure(EditorView.editable.of(editable))
      ]
    });
  }
  updateCommentsUi();
}

function renderPresence() {
  elements.presenceList.replaceChildren();
  if (!state.awareness) return;
  const people = [...state.awareness.getStates().entries()]
    .map(([id, value]) => ({ id, ...(value.user || {}) }))
    .filter((person) => person.name);
  for (const person of people.slice(0, 5)) {
    const avatar = document.createElement("span");
    avatar.className = "presence-avatar";
    avatar.style.setProperty("--presence-color", person.color || colorFor(String(person.id)));
    avatar.textContent = initials(person.name);
    avatar.title = person.name;
    elements.presenceList.append(avatar);
  }
  if (people.length > 5) {
    const more = document.createElement("span");
    more.className = "presence-more";
    more.textContent = `+${people.length - 5}`;
    elements.presenceList.append(more);
  }
}

function toggleCommentsPanel() {
  const open = elements.commentsPanel.dataset.open !== "true";
  elements.commentsPanel.dataset.open = String(open);
  elements.commentsToggle.setAttribute("aria-expanded", String(open));
  if (open) void refreshComments();
}

function closeCommentsPanel() {
  elements.commentsPanel.dataset.open = "false";
  elements.commentsToggle.setAttribute("aria-expanded", "false");
  cancelComment();
}

function updateCommentsUi() {
  elements.commentsToggle.disabled = !state.current;
  elements.addComment.disabled = !state.current || !state.canComment;
  elements.addComment.title = state.canComment
    ? "Select screenplay text, then add a comment"
    : "Sign in to comment";
}

async function refreshComments() {
  if (!state.current) return;
  const documentId = state.current.id;
  try {
    const payload = await api(`/api/documents/${encodeURIComponent(documentId)}/comments`);
    if (state.current?.id !== documentId) return;
    state.comments = payload.comments;
    state.canComment = payload.canComment;
    renderComments();
    updateCommentsUi();
    updateCommentMarks();
  } catch (error) {
    if (elements.commentsPanel.dataset.open === "true") {
      showToast(error.message || "Comments could not be loaded.");
    }
  }
}

function startComment() {
  if (!state.canComment) {
    showToast("Sign in to comment on this screenplay.");
    return;
  }
  const selection = state.view?.state.selection.main;
  if (!selection || selection.empty || !state.ytext) {
    showToast("Select screenplay text before adding a comment.");
    return;
  }
  const quotedText = state.view.state.doc.sliceString(selection.from, selection.to).trim();
  if (!quotedText) {
    showToast("Select screenplay text before adding a comment.");
    return;
  }
  state.pendingComment = {
    anchorStart: encodeCommentAnchor(selection.from, -1),
    anchorEnd: encodeCommentAnchor(selection.to, 1),
    quotedText: quotedText.slice(0, 500)
  };
  elements.commentsPanel.dataset.open = "true";
  elements.commentsToggle.setAttribute("aria-expanded", "true");
  elements.commentQuote.textContent = state.pendingComment.quotedText;
  elements.commentForm.hidden = false;
  elements.commentBody.value = "";
  elements.commentBody.focus();
}

function cancelComment() {
  state.pendingComment = null;
  elements.commentForm.hidden = true;
  elements.commentBody.value = "";
}

async function submitComment(event) {
  event.preventDefault();
  if (!state.current || !state.pendingComment) return;
  try {
    await api(`/api/documents/${encodeURIComponent(state.current.id)}/comments`, {
      method: "POST",
      body: JSON.stringify({
        ...state.pendingComment,
        body: elements.commentBody.value
      })
    });
    cancelComment();
    await refreshComments();
    showToast("Comment added.");
  } catch (error) {
    showToast(error.message || "The comment could not be added.");
  }
}

function renderComments() {
  elements.commentsList.replaceChildren();
  const visible = state.comments.filter(
    (comment) => comment.status === "open" || elements.showResolved.checked
  );
  const openCount = state.comments.filter((comment) => comment.status === "open").length;
  elements.commentCount.textContent = String(openCount);
  elements.commentCount.hidden = openCount === 0;

  if (!visible.length) {
    const empty = document.createElement("p");
    empty.className = "comment-empty";
    empty.textContent = state.comments.length
      ? "No open comments. Show resolved threads to revisit them."
      : "No comments yet. Select a passage in the editor to start a thread.";
    elements.commentsList.append(empty);
    return;
  }

  for (const comment of visible) {
    const thread = document.createElement("article");
    thread.className = "comment-thread";
    thread.dataset.status = comment.status;

    const header = document.createElement("div");
    header.className = "comment-thread-header";
    const author = document.createElement("strong");
    author.textContent = comment.authorName;
    const time = document.createElement("time");
    time.dateTime = comment.createdAt;
    time.textContent = formatRelativeTime(comment.createdAt);
    header.append(author, time);

    const quote = document.createElement("button");
    quote.type = "button";
    quote.className = "comment-quote";
    quote.textContent = comment.quotedText;
    quote.title = "Go to commented text";
    quote.addEventListener("click", () => navigateToComment(comment));

    const body = document.createElement("p");
    body.textContent = comment.body;
    const actions = document.createElement("div");
    actions.className = "comment-thread-actions";

    if (state.canComment && comment.status === "open") {
      const reply = document.createElement("button");
      reply.type = "button";
      reply.textContent = "Reply";
      reply.addEventListener("click", () => showReplyForm(thread, comment));
      actions.append(reply);
    }
    if (comment.canResolve) {
      const status = document.createElement("button");
      status.type = "button";
      status.textContent = comment.status === "open" ? "Resolve" : "Reopen";
      status.addEventListener("click", () => void setCommentStatus(
        comment,
        comment.status === "open" ? "resolved" : "open"
      ));
      actions.append(status);
    }

    thread.append(header, quote, body);
    if (actions.childElementCount) thread.append(actions);
    if (comment.replies.length) {
      const replies = document.createElement("div");
      replies.className = "comment-replies";
      for (const reply of comment.replies) {
        const item = document.createElement("div");
        item.className = "comment-reply";
        const meta = document.createElement("div");
        meta.className = "comment-reply-meta";
        const replyAuthor = document.createElement("strong");
        replyAuthor.textContent = reply.authorName;
        const replyTime = document.createElement("time");
        replyTime.dateTime = reply.createdAt;
        replyTime.textContent = formatRelativeTime(reply.createdAt);
        const replyBody = document.createElement("p");
        replyBody.textContent = reply.body;
        meta.append(replyAuthor, replyTime);
        item.append(meta, replyBody);
        replies.append(item);
      }
      thread.append(replies);
    }
    elements.commentsList.append(thread);
  }
}

function showReplyForm(thread, comment) {
  thread.querySelector(".comment-reply-form")?.remove();
  const form = document.createElement("form");
  form.className = "comment-reply-form";
  const textarea = document.createElement("textarea");
  textarea.rows = 2;
  textarea.maxLength = 2000;
  textarea.required = true;
  textarea.placeholder = "Reply to this thread";
  textarea.setAttribute("aria-label", `Reply to ${comment.authorName}`);
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "primary-button";
  submit.textContent = "Reply";
  form.append(textarea, submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      await api(
        `/api/documents/${encodeURIComponent(state.current.id)}/comments/${encodeURIComponent(comment.id)}/replies`,
        { method: "POST", body: JSON.stringify({ body: textarea.value }) }
      );
      await refreshComments();
    } catch (error) {
      showToast(error.message || "The reply could not be added.");
    }
  });
  thread.append(form);
  textarea.focus();
}

async function setCommentStatus(comment, status) {
  try {
    await api(
      `/api/documents/${encodeURIComponent(state.current.id)}/comments/${encodeURIComponent(comment.id)}`,
      { method: "PATCH", body: JSON.stringify({ status }) }
    );
    await refreshComments();
  } catch (error) {
    showToast(error.message || "The comment status could not be changed.");
  }
}

function navigateToComment(comment) {
  const range = resolveCommentRange(comment);
  if (!range || !state.view) {
    showToast("The original passage was removed.");
    return;
  }
  if (matchMedia("(max-width: 900px)").matches) setMobileView("editor");
  state.view.dispatch({
    selection: { anchor: range.from, head: range.to },
    scrollIntoView: true
  });
  state.view.focus();
}

function updateCommentMarks() {
  if (!state.view || !state.ydoc || !state.ytext) return;
  const ranges = state.comments
    .filter((comment) => comment.status === "open")
    .map(resolveCommentRange)
    .filter(Boolean)
    .map(({ from, to }) => Decoration.mark({ class: "cm-comment-range" }).range(from, to))
    .sort((left, right) => left.from - right.from || left.to - right.to);
  state.view.dispatch({
    effects: setCommentDecorations.of(Decoration.set(ranges, true))
  });
}

function resolveCommentRange(comment) {
  if (!state.ydoc || !state.ytext) return null;
  try {
    const start = Y.createAbsolutePositionFromRelativePosition(
      Y.decodeRelativePosition(fromBase64Url(comment.anchorStart)),
      state.ydoc
    );
    const end = Y.createAbsolutePositionFromRelativePosition(
      Y.decodeRelativePosition(fromBase64Url(comment.anchorEnd)),
      state.ydoc
    );
    if (!start || !end || start.type !== state.ytext || end.type !== state.ytext) return null;
    const from = Math.max(0, Math.min(start.index, state.view.state.doc.length));
    const to = Math.max(from, Math.min(end.index, state.view.state.doc.length));
    return to > from ? { from, to } : null;
  } catch {
    return null;
  }
}

function encodeCommentAnchor(index, assoc) {
  const relative = Y.createRelativePositionFromTypeIndex(state.ytext, index, assoc);
  return toBase64Url(Y.encodeRelativePosition(relative));
}

function toBase64Url(value) {
  let binary = "";
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function fromBase64Url(value) {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

async function openShareDialog() {
  if (!state.current || state.role !== "owner") return;
  elements.newShareLink.hidden = true;
  elements.shareDialog.showModal();
  await refreshSharing();
}

async function refreshSharing() {
  const id = encodeURIComponent(state.current.id);
  const [shares, links] = await Promise.all([
    api(`/api/documents/${id}/shares`),
    api(`/api/documents/${id}/share-links`)
  ]);
  renderShares(shares.shares);
  renderShareLinks(links.links);
}

function renderShares(shares) {
  elements.shareList.replaceChildren();
  if (!shares.length) {
    elements.shareList.append(emptyShareRow("Only you have access."));
    return;
  }
  for (const share of shares) {
    const row = document.createElement("div");
    row.className = "share-row";
    const identity = document.createElement("div");
    identity.innerHTML = `<strong>${escapeHtml(share.email)}</strong><small>${
      share.email_status === "failed"
        ? `Invitation failed: ${escapeHtml(share.email_error || "delivery error")}`
        : share.accepted_at
          ? "Accessed"
          : "Invitation pending"
    }</small>`;
    const role = roleSelect(share.role, async (nextRole) => {
      await api(`/api/documents/${encodeURIComponent(state.current.id)}/shares/${encodeURIComponent(share.email)}`, {
        method: "PATCH",
        body: JSON.stringify({ role: nextRole })
      });
      await refreshSharing();
    });
    const resend = document.createElement("button");
    resend.type = "button";
    resend.textContent = share.email_status === "failed" ? "Resend" : "Send again";
    resend.addEventListener("click", async () => {
      await api(`/api/documents/${encodeURIComponent(state.current.id)}/shares`, {
        method: "POST",
        body: JSON.stringify({ email: share.email, role: role.value })
      });
      await refreshSharing();
    });
    const revoke = document.createElement("button");
    revoke.type = "button";
    revoke.className = "danger-button";
    revoke.textContent = "Revoke";
    revoke.addEventListener("click", async () => {
      await api(`/api/documents/${encodeURIComponent(state.current.id)}/shares/${encodeURIComponent(share.email)}`, {
        method: "DELETE"
      });
      await refreshSharing();
    });
    row.append(identity, role, resend, revoke);
    elements.shareList.append(row);
  }
}

function renderShareLinks(links) {
  elements.shareLinkList.replaceChildren();
  if (!links.length) {
    elements.shareLinkList.append(emptyShareRow("No active share links."));
    return;
  }
  for (const link of links) {
    const row = document.createElement("div");
    row.className = "share-row";
    const identity = document.createElement("div");
    const expired = link.expires_at && new Date(link.expires_at) <= new Date();
    identity.innerHTML = `<strong>${capitalize(link.role)} link</strong><small>${
      link.revoked_at ? "Revoked" : expired ? "Expired" : link.expires_at ? `Expires ${new Date(link.expires_at).toLocaleDateString()}` : "No expiration"
    }</small>`;
    const revoke = document.createElement("button");
    revoke.type = "button";
    revoke.className = "danger-button";
    revoke.textContent = "Revoke";
    revoke.disabled = Boolean(link.revoked_at);
    revoke.addEventListener("click", async () => {
      await api(`/api/documents/${encodeURIComponent(state.current.id)}/share-links/${encodeURIComponent(link.id)}`, {
        method: "DELETE"
      });
      await refreshSharing();
    });
    row.append(identity, revoke);
    elements.shareLinkList.append(row);
  }
}

async function inviteCollaborator(event) {
  event.preventDefault();
  const email = document.querySelector("#invite-email").value;
  const role = document.querySelector("#invite-role").value;
  try {
    const payload = await api(`/api/documents/${encodeURIComponent(state.current.id)}/shares`, {
      method: "POST",
      body: JSON.stringify({ email, role })
    });
    document.querySelector("#invite-email").value = "";
    showToast(payload.share.emailStatus === "failed"
      ? "Access was granted, but invitation delivery failed."
      : "Invitation sent.");
    await refreshSharing();
  } catch (error) {
    showToast(error.message || "The invitation could not be created.");
  }
}

async function createShareLink(event) {
  event.preventDefault();
  try {
    const payload = await api(`/api/documents/${encodeURIComponent(state.current.id)}/share-links`, {
      method: "POST",
      body: JSON.stringify({
        role: document.querySelector("#link-role").value,
        expiresIn: document.querySelector("#link-expiration").value
      })
    });
    elements.newShareLinkUrl.value = payload.link.url;
    elements.newShareLink.hidden = false;
    await refreshSharing();
  } catch (error) {
    showToast(error.message || "The share link could not be created.");
  }
}

async function copyNewShareLink() {
  await navigator.clipboard.writeText(elements.newShareLinkUrl.value);
  showToast("Share link copied.");
}

function roleSelect(value, onChange) {
  const select = document.createElement("select");
  select.setAttribute("aria-label", "Collaborator role");
  select.append(new Option("Editor", "editor"), new Option("Viewer", "viewer"));
  select.value = value;
  select.addEventListener("change", () => void onChange(select.value));
  return select;
}

function emptyShareRow(text) {
  const row = document.createElement("p");
  row.className = "empty-share-row";
  row.textContent = text;
  return row;
}

function handleAccessChanged(reason) {
  if (state.accessChanged) return;
  state.accessChanged = true;
  state.role = "viewer";
  updateRoleUi();
  setConnectionState("access", "Access changed");
  elements.accessMessage.textContent = `${reason} You can preserve the copy currently stored on this device.`;
  if (!elements.accessDialog.open) elements.accessDialog.showModal();
}

async function saveAccessCopy() {
  try {
    const created = await createDocument(`${normalizedName(state.current.name)} Recovered Copy`, currentSource());
    elements.accessDialog.close();
    await openDocument(created.id, created);
  } catch (error) {
    if (error instanceof ApiRequestError && (error.status === 401 || error.status === 402)) {
      downloadCue();
      showToast("A Cue file was downloaded because this account cannot create a screenplay.");
      return;
    }
    showToast(error.message || "The recovery copy could not be saved.");
  }
}

function downloadCue() {
  if (!state.current) return;
  downloadBlob(`${safeFilename(state.current.name)}.cue`, currentSource(), "text/plain;charset=utf-8");
}

function downloadHtml() {
  if (!state.current) return;
  const html = renderHtml(parse(currentSource()));
  downloadBlob(`${safeFilename(state.current.name)}.html`, html, "text/html;charset=utf-8");
}

function printPreview() {
  if (!state.current) return;
  const html = renderHtml(parse(currentSource()));
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const printWindow = window.open(url, "_blank", "noopener");
  if (!printWindow) {
    URL.revokeObjectURL(url);
    showToast("Allow pop-ups to print the rendered screenplay.");
    return;
  }
  printWindow.addEventListener("load", () => {
    printWindow.print();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
}

function downloadBlob(filename, content, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function handleKeyboardShortcut(event) {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
  const key = event.key.toLowerCase();
  if (key === "s") {
    event.preventDefault();
    void saveCurrent();
  } else if (key === "n") {
    event.preventDefault();
    void newDocument();
  } else if (key === "o") {
    event.preventDefault();
    chooseFile("cue");
  } else if (key === "p") {
    event.preventDefault();
    printPreview();
  }
}

function currentSource() {
  if (state.ytext) return state.ytext.toString();
  return state.current?.source || "";
}

function canEdit() {
  return state.role === "owner" || state.role === "editor";
}

function setConnectionState(status, label) {
  elements.saveState.dataset.state = status;
  elements.saveLabel.textContent = label;
}

function updateStats(source = currentSource()) {
  const trimmed = source.trim();
  const words = trimmed ? trimmed.split(/\s+/).length : 0;
  elements.sourceStats.textContent = `${words.toLocaleString()} words · ${source.length.toLocaleString()} characters`;
}

function setMobileView(view) {
  elements.splitWorkspace.dataset.mobileView = view;
  for (const tab of document.querySelectorAll("[data-mobile-view]")) {
    if (tab !== elements.splitWorkspace) tab.setAttribute("aria-selected", String(tab.dataset.mobileView === view));
  }
  if (view === "preview") requestAnimationFrame(scrollPreviewToLocalEdit);
}

function toggleMobileLibrary() {
  const open = elements.libraryPanel.dataset.open !== "true";
  elements.libraryPanel.dataset.open = String(open);
  elements.mobileLibraryButton.setAttribute("aria-expanded", String(open));
}

function closeMobileLibrary() {
  elements.libraryPanel.dataset.open = "false";
  elements.mobileLibraryButton.setAttribute("aria-expanded", "false");
}

function deriveDocumentName(source, filename) {
  try {
    const title = parse(source).metadata.title?.trim();
    if (title) return title.slice(0, 120);
  } catch {
    // Preserve imports even if their metadata is incomplete.
  }
  return filename.replace(/\.(?:cue|trelby)$/i, "").slice(0, 120) || "Imported Screenplay";
}

function normalizedName(value) {
  return String(value || "").trim().slice(0, 120) || "Untitled Screenplay";
}

function safeFilename(value) {
  return normalizedName(value).replace(/[<>:"/\\|?*\u0000-\u001F]/g, "-").replace(/\.+$/, "");
}

function formatRelativeTime(value) {
  if (!value) return "Just now";
  const deltaMinutes = Math.round((new Date(value).getTime() - Date.now()) / 60000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(deltaMinutes) < 60) return formatter.format(deltaMinutes, "minute");
  const deltaHours = Math.round(deltaMinutes / 60);
  if (Math.abs(deltaHours) < 24) return formatter.format(deltaHours, "hour");
  return formatter.format(Math.round(deltaHours / 24), "day");
}

function summaryFromDocument(documentData) {
  const { source: _source, ...summary } = documentData;
  return summary;
}

function upsertSummary(documentData) {
  const summary = summaryFromDocument(documentData);
  const index = state.documents.findIndex((item) => item.id === summary.id);
  if (index >= 0) state.documents[index] = { ...state.documents[index], ...summary };
  else state.documents.unshift(summary);
}

function localDisplayName() {
  return "You";
}

function colorFor(value) {
  const colors = ["#f07167", "#00a896", "#3a86ff", "#9b5de5", "#f59e0b", "#e85d75", "#2a9d8f"];
  let hash = 0;
  for (const character of value) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return colors[hash % colors.length];
}

function initials(name) {
  return String(name).split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

function capitalize(value) {
  return value ? value[0].toUpperCase() + value.slice(1) : "";
}

function showToast(message) {
  clearTimeout(state.toastTimer);
  elements.toast.textContent = message;
  elements.toast.hidden = false;
  state.toastTimer = setTimeout(() => {
    elements.toast.hidden = true;
  }, 4200);
}

function handleNetworkError(error, fallback) {
  setConnectionState("offline", "Offline");
  showToast(error instanceof Error ? error.message : fallback);
}

async function api(path, options = {}) {
  const headers = new Headers(options.headers);
  if (options.body) headers.set("Content-Type", "application/json");
  headers.set("Accept", "application/json");
  const response = await fetch(`${APP_BASE}${path}`, { ...options, headers, credentials: "same-origin" });
  if (response.status === 204) return null;
  let payload;
  try {
    payload = await response.json();
  } catch {
    throw new ApiRequestError(response.status, "invalid_response", "The server returned an invalid response.");
  }
  if (!response.ok) {
    throw new ApiRequestError(
      response.status,
      payload.error?.code ?? "request_failed",
      payload.error?.message ?? "The request failed.",
      payload.error
    );
  }
  return payload;
}

class ApiRequestError extends Error {
  constructor(status, code, message, details = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
